import Segmented from '@/components/base/Segmented';
import { PROFIT_PERIOD_OPTIONS, type ProfitPeriodMode } from '@/lib/profits';

export interface CashierOption {
  id: string;
  name: string;
  role: string;
}

interface ProfitFiltersProps {
  mode: ProfitPeriodMode;
  customStart: string;
  customEnd: string;
  onMode: (mode: ProfitPeriodMode) => void;
  onCustomStart: (value: string) => void;
  onCustomEnd: (value: string) => void;
  isAdmin: boolean;
  branchId: string | null;
  onBranch: (value: string | null) => void;
  branches: { id: string; name: string }[];
  cashierId: string | null;
  onCashier: (value: string | null) => void;
  cashiers: CashierOption[];
  categoryId: string | null;
  onCategory: (value: string | null) => void;
  categories: { id: string; name: string }[];
  productQuery: string;
  onProductQuery: (value: string) => void;
}

const selectClass =
  'rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer';

export default function ProfitFilters(props: ProfitFiltersProps) {
  const {
    mode,
    customStart,
    customEnd,
    onMode,
    onCustomStart,
    onCustomEnd,
    isAdmin,
    branchId,
    onBranch,
    branches,
    cashierId,
    onCashier,
    cashiers,
    categoryId,
    onCategory,
    categories,
    productQuery,
    onProductQuery,
  } = props;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          options={PROFIT_PERIOD_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={mode}
          onChange={(v) => onMode(v as ProfitPeriodMode)}
        />
        {mode === 'custom' && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={customStart}
              onChange={(e) => onCustomStart(e.target.value)}
              className={selectClass}
            />
            <span className="text-foreground-500 text-sm">a</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => onCustomEnd(e.target.value)}
              className={selectClass}
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {isAdmin && (
          <select value={branchId ?? ''} onChange={(e) => onBranch(e.target.value || null)} className={selectClass}>
            <option value="">Todas las sucursales</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}

        {isAdmin && (
          <select value={cashierId ?? ''} onChange={(e) => onCashier(e.target.value || null)} className={selectClass}>
            <option value="">Todos los usuarios</option>
            {cashiers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {c.role}
              </option>
            ))}
          </select>
        )}

        <select value={categoryId ?? ''} onChange={(e) => onCategory(e.target.value || null)} className={selectClass}>
          <option value="">Todas las categorías</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            value={productQuery}
            onChange={(e) => onProductQuery(e.target.value)}
            placeholder="Buscar producto o SKU"
            className="rounded-md border border-background-300 bg-background-50 pl-9 pr-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 w-56"
          />
        </div>
      </div>
    </div>
  );
}